<template>
  <div class="access-groups-form">
    <OurGroup>
      <OurInput
        v-model="form.title"
        :label="$t('survey.title')"
        :placeholder="$t('survey.title')"
        :error="titleError"
      />
      <OurTextarea
        v-model="form.text"
        :label="$t('survey.text')"
        :placeholder="$t('survey.text')"
        rows="4"
        :error="textError"
      />
      <OurInput
        v-model="form.buttonText"
        :label="$t('survey.buttonText')"
        :placeholder="$t('survey.buttonText')"
        :error="buttonTextError"
      />
      <OurInput
        v-model="form.url"
        :label="$t('survey.url')"
        :placeholder="$t('survey.urlPlaceholder')"
        :error="urlError"
      />
      <OurGroup class="!mt-4" :title="$t('survey.validityPeriod')">
        <div class="flex flex-col gap-4 sm:flex-row">
          <OurDatePicker
            v-model="form.startsAt"
            class="flex-1"
            :label="$t('survey.validityFrom')"
            :error="startsAtError"
          />
          <OurDatePicker v-model="form.endsAt" class="flex-1" :label="$t('survey.validityTo')" :error="endsAtError" />
        </div>
      </OurGroup>
      <OurInput
        v-model="form.maxShows"
        validation-rule="number"
        :label="$t('survey.maxShows')"
        :placeholder="$t('survey.maxShows')"
        :error="maxShowsError"
        :description="$t('survey.maxShowsHint')"
      />
    </OurGroup>
  </div>
</template>

<script>
import { required, url, minValue, maxValue, maxLength } from "vuelidate/lib/validators";
import ValidationService from "@/services/validation.service";

const { TEXT_MAX_LENGTH, SURVEY_ADDITIONAL_TEXT_MAX_LENGTH } = ValidationService;

const validDateRange = function validDateRange(endsAt) {
  const { startsAt } = this.form;

  if (!startsAt || !endsAt) {
    return true;
  }

  return Number(endsAt) > Number(startsAt);
};

export default {
  name: "SurveyForm",

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      form: {
        title: "",
        text: "",
        buttonText: "Детальніше",
        url: "",
        startsAt: null,
        endsAt: null,
        maxShows: 1,
      },
    };
  },

  validations: {
    form: {
      title: { required, maxLength: maxLength(TEXT_MAX_LENGTH) },
      text: { required, maxLength: maxLength(SURVEY_ADDITIONAL_TEXT_MAX_LENGTH) },
      buttonText: { required, maxLength: maxLength(32) },
      url: { required, url },
      startsAt: { required },
      endsAt: { required, validDateRange },
      maxShows: { required, minValue: minValue(1), maxValue: maxValue(10) },
    },
  },

  computed: {
    titleError() {
      return this.getFieldError("title", TEXT_MAX_LENGTH);
    },

    textError() {
      return this.getFieldError("text", SURVEY_ADDITIONAL_TEXT_MAX_LENGTH);
    },

    buttonTextError() {
      return this.getFieldError("buttonText", 32);
    },

    urlError() {
      const isDirty = this.$v.form.url.$dirty;
      const isFilled = this.$v.form.url.required;
      const isUrl = this.$v.form.url.url;

      if (isDirty && !isFilled) {
        return this.$t("validation.required");
      }

      if (isDirty && !isUrl) {
        return this.$t("survey.urlInvalid");
      }

      return "";
    },

    startsAtError() {
      const isDirty = this.$v.form.startsAt.$dirty;
      const isFilled = this.$v.form.startsAt.required;

      if (isDirty && !isFilled) {
        return this.$t("validation.required");
      }

      return "";
    },

    endsAtError() {
      const isDirty = this.$v.form.endsAt.$dirty;
      const isFilled = this.$v.form.endsAt.required;
      const isValidRange = this.$v.form.endsAt.validDateRange;

      if (isDirty && !isFilled) {
        return this.$t("validation.required");
      }

      if (isDirty && !isValidRange) {
        return this.$t("survey.endsAtAfterStartsAt");
      }

      return "";
    },

    maxShowsError() {
      const isDirty = this.$v.form.maxShows.$dirty;
      const isFilled = this.$v.form.maxShows.required;
      const min = this.$v.form.maxShows.minValue;
      const max = this.$v.form.maxShows.maxValue;

      if (isDirty && !isFilled) {
        return this.$t("validation.required");
      }

      if (isDirty && (!min || !max)) {
        return this.$t("survey.maxShowsRange");
      }

      return "";
    },
  },

  watch: {
    value: {
      handler: "getForm",
      deep: true,
    },
  },

  methods: {
    getForm(value) {
      if (value && Object.keys(value).length) {
        this.form = { ...this.form, ...value };
      }
    },

    getFieldError(fieldName, maxLen) {
      const field = this.$v.form[fieldName];
      const isDirty = field.$dirty;
      const isFilled = field.required;
      const withinMaxLength = field.maxLength;

      if (isDirty && !isFilled) {
        return this.$t("validation.required");
      }

      if (isDirty && !withinMaxLength) {
        return this.$t("validation.textMaxLength", { length: maxLen });
      }

      return "";
    },

    emitForm() {
      if (this.$v.$invalid) {
        this.$v.$touch();

        return;
      }

      const maxShows = Number(this.form.maxShows) || 1;

      this.form.isValid = true;
      this.$emit("input", {
        ...this.form,
        maxShows,
        startsAt: Number(this.form.startsAt),
        endsAt: Number(this.form.endsAt),
      });
    },
  },
};
</script>
